"use client";

import { useRouter } from "next/navigation";
import { useRef, useState, useReducer, useMemo, useCallback, useEffect } from "react";
import useAuth from "~/services/auth/use-auth";
import { Client } from "persona";
import { PersonaContext } from "./persona-context";
import useAuthActions from "~/services/auth/use-auth-actions";
import { PERSONA_TEMPLATE, WITH_PERSONA_ENV_ID } from "~/services/api/config";
import { APP_ROUTES } from "~/constants/appRoutes";
import {
    isUnsuccessfulInquiryStatus,
    PERSONA_VERIFICATION_STATUS,
    type PersonaVerificationStatus,
} from "./constants";

const HISTORY_STATE_KEY = "personaWidgetOpen";

const hasHistoryEntry = () => Boolean(window.history.state?.[HISTORY_STATE_KEY]);

export const PersonaProvider = ({ children }) => {
    const router = useRouter();
    const { user } = useAuth();
    const { setUser } = useAuthActions();

    const [options] = useState({
        templateId: PERSONA_TEMPLATE,
        environmentId: WITH_PERSONA_ENV_ID,
    });

    const [verificationStatus, setVerificationStatus] =
        useState<PersonaVerificationStatus>(PERSONA_VERIFICATION_STATUS.IDLE);
    const [, dispatch] = useReducer((currLogs, action) => {
        switch (action.type) {
            case "append":
                return currLogs.concat(action.log);
            case "clear":
                return [];
            default:
                return currLogs;
        }
    }, []);

    const embeddedClientRef = useRef<null | Client>(null);
    const isWidgetOpenRef = useRef(false);

    // The widget is a fixed overlay rather than a route, so back would otherwise
    // navigate away from the page underneath it. A throwaway history entry pushed
    // on open lets popstate mean "dismiss the widget".
    const openWidget = useCallback((client: Client) => {
        isWidgetOpenRef.current = true;
        window.history.pushState({ [HISTORY_STATE_KEY]: true }, "");
        client.open();
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            if (!isWidgetOpenRef.current) return;
            isWidgetOpenRef.current = false;
            // Forced so Persona exits straight away instead of prompting.
            embeddedClientRef.current?.cancel(true);
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    const createClient = useCallback(async () => {
        const Persona = (await import("persona")).default;
        const client = new Persona.Client({
            templateId: options.templateId,
            environmentId: options.environmentId,
            referenceId: user?.id,
            fields: {
                nameFirst: user?.firstName || "",
                nameLast: user?.lastName || "",
                birthdate: user?.dateOfBirth || "",
            },
            onLoad: () => {
                // If calling client.open() here, widget will open sooner and show a loading state.
                openWidget(client);
            },
            onReady: () => {
                // If calling client.open() here, widget will open later and be immediately ready to use.
                // This should be done if loading state is handled externally.
                // client.open();
            },
            onComplete: async ({ status }) => {
                setVerificationStatus(PERSONA_VERIFICATION_STATUS.IDLE);
                isWidgetOpenRef.current = false;

                if (isUnsuccessfulInquiryStatus(status)) {
                    if (hasHistoryEntry()) {
                        window.history.back();
                    }
                    return;
                }

                setUser((currentUser) => {
                    if (!currentUser) {
                        return currentUser;
                    }

                    return {
                        ...currentUser,
                        idCardVerifiedAt: new Date().toISOString(),
                    };
                });

                // Replacing consumes the throwaway entry, so back from the
                // confirmation page returns to the page that opened the widget.
                if (hasHistoryEntry()) {
                    router.replace(APP_ROUTES.THANKS_FOR_ID_UPLOAD);
                } else {
                    router.push(APP_ROUTES.THANKS_FOR_ID_UPLOAD);
                }
            },
            onCancel: () => {
                setVerificationStatus(PERSONA_VERIFICATION_STATUS.IDLE);

                // Already unwound when the dismissal came from popstate itself.
                if (!isWidgetOpenRef.current) return;
                isWidgetOpenRef.current = false;

                if (hasHistoryEntry()) {
                    window.history.back();
                }
            },
            onError: (error) => {
                setVerificationStatus(PERSONA_VERIFICATION_STATUS.IDLE);
                console.log(error);
            },
            onEvent: (name, meta) => {
                dispatch({ type: "append", log: { name, meta } });
            },
        });

        embeddedClientRef.current = client;
        window.cancel = (force) => {
            if (client) {
                client.cancel(force);
                setVerificationStatus(PERSONA_VERIFICATION_STATUS.IDLE);
            } else {
                console.log("Initialize client first");
            }
        };
    }, [
        options.environmentId,
        options.templateId,
        openWidget,
        router,
        setUser,
        user,
    ]);

    const verifyId = useCallback(() => {
        if (!user) {
            return;
        }

        setVerificationStatus(PERSONA_VERIFICATION_STATUS.IN_PROGRESS);

        if (embeddedClientRef.current === null) {
            void createClient().catch((error) => {
                setVerificationStatus(PERSONA_VERIFICATION_STATUS.IDLE);
                console.log(error);
            });
        } else {
            openWidget(embeddedClientRef.current);
        }
    }, [embeddedClientRef, user, createClient, openWidget]);

    const contextValue = useMemo(
        () => ({
            verifyId,
            verificationStatus,
            isVerificationInProgress:
                verificationStatus === PERSONA_VERIFICATION_STATUS.IN_PROGRESS,
        }),
        [verifyId, verificationStatus]
    );

    return (
        <PersonaContext.Provider value={contextValue}>
            {children}
        </PersonaContext.Provider>
    );
};
